import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { commonsConfig } from '../config'
import { useLogout, type CurrentUser } from '../lib/session'
import SessionTimeoutWarning from './SessionTimeoutWarning'

const navLink =
  'rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
const activeNavLink = 'bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50'

export default function AppLayout({
  user,
  children,
}: {
  user: CurrentUser
  children: ReactNode
}) {
  const logout = useLogout()

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <header className="border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-1">
            <Link
              to="/"
              className="mr-3 text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-50"
            >
              {commonsConfig().appName}
            </Link>
            <nav className="flex items-center gap-1" aria-label="Navigation principale">
              <Link
                to="/"
                className={navLink}
                activeProps={{ className: activeNavLink }}
                activeOptions={{ exact: true }}
              >
                Accueil
              </Link>
              {commonsConfig().navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={navLink}
                  activeProps={{ className: activeNavLink }}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/profile" className={navLink} activeProps={{ className: activeNavLink }}>
                Profil
              </Link>
              {user.isAdmin && <AdminMenu />}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-600 dark:text-neutral-400">{user.displayName}</span>
            <button
              type="button"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className={`${navLink} disabled:opacity-50`}
            >
              Se deconnecter
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">{children}</main>

      <SessionTimeoutWarning />
    </div>
  )
}

// Confort d'affichage : le serveur refuse /api/admin/* a tout non-administrateur.
function AdminMenu() {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !container.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        className={navLink}
      >
        Admin ▾
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 z-10 mt-1 min-w-48 rounded-lg border border-neutral-200 bg-white p-1 shadow-lg dark:border-neutral-800 dark:bg-neutral-900"
        >
          {[{ to: '/admin/users', label: 'Gestion des utilisateurs' }, ...commonsConfig().adminLinks].map(
            (link) => (
              <Link
                key={link.to}
                to={link.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="block rounded-md px-3 py-2 text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                {link.label}
              </Link>
            ),
          )}
        </div>
      )}
    </div>
  )
}
